import { Suspense, lazy, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

const ExtendSpreadsheet = lazy(() => import('./ExtendSpreadsheet'))

const loading = (
  <div className="flex h-32 items-center justify-center">
    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
  </div>
)

/**
 * Reads the workbook in the browser, through a WebAssembly engine that is
 * fetched on demand. It is a couple of megabytes, so it is deliberately not in
 * the bundle every page pays for: only someone looking at a spreadsheet waits
 * for it, once, and their browser keeps it afterwards.
 */
export function SpreadsheetViewer({ blob, fileName }: { blob: Blob | null; fileName: string }) {
  const [file, setFile] = useState<ArrayBuffer | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setFile(null)
    setError(null)

    if (!blob) return

    void blob
      .arrayBuffer()
      .then((bytes) => {
        if (!cancelled) setFile(bytes)
      })
      .catch(() => {
        if (!cancelled) setError('Could not read this workbook.')
      })

    return () => {
      cancelled = true
    }
  }, [blob])

  if (error) {
    return <p className="px-4 py-6 text-center text-xs text-gray-400">{error}</p>
  }

  if (!file) return loading

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Suspense fallback={loading}>
        <ExtendSpreadsheet file={file} fileName={fileName} />
      </Suspense>
    </div>
  )
}
